import React, { useEffect, useMemo, useState } from 'react';
import { DirectionsRenderer, GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Field,
  Header,
  HeaderIconButton,
  HeaderText,
  Message,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import { isPersonalApp } from '../utils/appMode';
import useBackNavigation from '../utils/useBackNavigation';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.75rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 0.55rem;
  }
`;

const MapPanel = styled(Card)`
  padding: 0;
  overflow: hidden;
  margin-bottom: 1rem;
  position: relative;
`;

const MapContainer = styled.div`
  height: min(58vh, 480px);
  min-height: 340px;
`;

const MapControls = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  display: flex;
  gap: 0.45rem;
  align-items: center;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #111827;
  font: inherit;
  font-size: 1.3rem;
  font-weight: 950;
  line-height: 1;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.24);
  cursor: pointer;

  &:disabled {
    opacity: 0.62;
    cursor: wait;
  }
`;

const MapCenterButton = styled(Button)`
  min-height: 44px;
  padding: 0.55rem 0.8rem;
  background: rgba(255, 255, 255, 0.96);
  color: #111827;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.24);
`;

const MapSetButton = styled(Button)`
  min-height: 44px;
  padding: 0.55rem 1rem;
  background: linear-gradient(135deg, #fbbf24, #fb923c);
  color: #111827;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.24);
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 0 0 1rem;

  @media (max-width: 760px) {
    gap: 0.3rem;
  }
`;

const Stat = styled.div`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.055);
  color: inherit;
  font: inherit;
  text-align: left;
  min-width: 0;

  ${({ $interactive }) => $interactive && `
    border-color: rgba(251, 191, 36, 0.42);
    background:
      linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 146, 60, 0.14)),
      rgba(255, 255, 255, 0.075);
    box-shadow: inset 0 0 0 1px rgba(251, 191, 36, 0.12);
    cursor: pointer;

    ${StatLabel} {
      color: #fde68a;
    }

    &:focus-visible {
      border-color: rgba(251, 191, 36, 0.72);
      box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.12);
      outline: none;
    }
  `}

  @media (max-width: 760px) {
    padding: 0.65rem 0.45rem;
    border-radius: 18px;
  }
`;

const StatLabel = styled.div`
  color: rgba(203, 213, 225, 0.66);
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 760px) {
    font-size: 0.52rem;
    letter-spacing: 0.045em;
  }
`;

const StatValue = styled.div`
  margin-top: 0.25rem;
  color: #f8fafc;
  font-size: clamp(1.7rem, 5vw, 2.7rem);
  font-weight: 950;
  line-height: 0.95;
  letter-spacing: -0.055em;
  white-space: nowrap;

  @media (max-width: 760px) {
    font-size: clamp(1.12rem, 6.8vw, 1.72rem);
    letter-spacing: -0.035em;
  }

  @media (max-width: 380px) {
    font-size: clamp(1rem, 6.4vw, 1.38rem);
  }
`;

const RunHeader = styled(Header)`
  position: relative;

  @media (max-width: 640px) {
    padding-right: 5.25rem;
  }
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;

  @media (max-width: 640px) {
    position: absolute;
    top: 0;
    right: 0;
  }
`;

const SliderField = styled(Field)`
  gap: 0.2rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.55);
  font-size: 0.68rem;
  letter-spacing: 0.06em;
`;

const SliderHeader = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SliderValue = styled.span`
  color: #f8fafc;
  font-size: 1.05rem;
  font-weight: 950;
  letter-spacing: 0;
  text-transform: none;
  white-space: nowrap;
`;

const PrimaryAction = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;
`;

const StartButton = styled(Button)`
  width: min(100%, 420px);
  min-height: 56px;
  font-size: 1.05rem;
`;

const RangeInput = styled.input`
  width: 100%;
  height: 30px;
  margin: 0.05rem 0 0;
  accent-color: #fbbf24;
  appearance: none;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 7px;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.18);
  }

  &::-webkit-slider-thumb {
    appearance: none;
    width: 22px;
    height: 22px;
    margin-top: -7.5px;
    border: 2px solid #111827;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.28);
  }

  &::-moz-range-track {
    height: 7px;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.18);
  }

  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border: 2px solid #111827;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.28);
  }

  &:focus-visible {
    outline: 3px solid rgba(251, 191, 36, 0.32);
    outline-offset: 6px;
    border-radius: 999px;
  }
`;

const FEET_PER_MILE = 5280;
const PACE_MINUTES_MIN = 4;
const PACE_MINUTES_MAX = 60;
const TIMER_MINUTES_MIN = 0.5;
const TIMER_MINUTES_MAX = 240;
const SLIDER_STEP_DECIMALS = 1;

const getApiUrl = () => {
  return window.RUNTIME_CONFIG?.apiUrl || process.env.REACT_APP_API_URL;
};

const isLocalTestHost = () => {
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const getFreshLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is unavailable'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      reject,
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000
      }
    );
  });
};

const getDistanceFeet = (start, end) => {
  if (!start || !end) {
    return null;
  }

  const radiusFeet = 20902231;
  const lat1 = start.lat * Math.PI / 180;
  const lat2 = end.lat * Math.PI / 180;
  const deltaLat = (end.lat - start.lat) * Math.PI / 180;
  const deltaLng = (end.lng - start.lng) * Math.PI / 180;
  const a = Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return radiusFeet * c;
};

const formatMiles = (feet) => {
  if (!Number.isFinite(feet)) {
    return '-';
  }

  return `${(feet / FEET_PER_MILE).toFixed(2)} mi`;
};

const getGoogleMapsApiKey = () => {
  return window.RUNTIME_CONFIG?.googleMaps?.apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
};

const formatNumberInput = (value, decimals = 1) => {
  if (!Number.isFinite(value)) {
    return '';
  }

  return value.toFixed(decimals).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
};

const clampNumber = (value, min, max) => {
  if (!Number.isFinite(value)) {
    return null;
  }

  return Math.min(max, Math.max(min, value));
};

const formatSliderInput = (value, min, max) => {
  const clampedValue = clampNumber(value, min, max);

  if (clampedValue === null) {
    return '';
  }

  return formatNumberInput(clampedValue, SLIDER_STEP_DECIMALS);
};

const formatDueTime = (date) => {
  if (!date) {
    return '-';
  }

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatSliderValue = (value, { prefix = '', suffix = '' } = {}) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '-';
  }

  return `${prefix}${formatNumberInput(number, 1)}${suffix}`;
};

const RunPledge = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/dashboard');
  const googleMapsApiKey = getGoogleMapsApiKey();
  const [startLocation, setStartLocation] = useState(null);
  const [targetLocation, setTargetLocation] = useState(null);
  const [directions, setDirections] = useState(null);
  const [routeDistanceFeet, setRouteDistanceFeet] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [paceMinutesPerMile, setPaceMinutesPerMile] = useState('10');
  const [timerMinutes, setTimerMinutes] = useState('');
  const [timingMode, setTimingMode] = useState('pace');
  const [amount, setAmount] = useState('5');
  const [showMap, setShowMap] = useState(false);
  const [mapConfirmed, setMapConfirmed] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const localTestHost = isLocalTestHost();
  const canUseRunPledge = isPersonalApp() && user?.isAdmin === true;

  const straightLineDistanceFeet = useMemo(() => {
    return getDistanceFeet(startLocation, targetLocation);
  }, [startLocation, targetLocation]);

  const distanceFeet = routeDistanceFeet || straightLineDistanceFeet;

  const distanceMiles = Number.isFinite(distanceFeet) ? distanceFeet / FEET_PER_MILE : null;
  const hasDistance = Number.isFinite(distanceFeet);
  const showPledgeControls = mapConfirmed || hasDistance;

  const dueAt = useMemo(() => {
    const minutes = Number(timerMinutes);

    if (!Number.isFinite(minutes) || minutes <= 0) {
      return null;
    }

    return new Date(Date.now() + minutes * 60 * 1000);
  }, [timerMinutes]);

  const mapCenter = startLocation || {
    lat: 37.7749,
    lng: -122.4194
  };

  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  useEffect(() => {
    if (!canUseRunPledge) {
      return;
    }

    setLoadingLocation(true);
    getFreshLocation()
      .then((location) => {
        setStartLocation(location);
      })
      .catch((locationError) => {
        console.error('Error getting location:', locationError);
        setError('Unable to get your current location. Enable location services and try again.');
      })
      .finally(() => {
        setLoadingLocation(false);
      });
  }, [canUseRunPledge]);

  useEffect(() => {
    setDirections(null);
    setRouteDistanceFeet(null);
    setRouteError('');

    if (!startLocation || !targetLocation || !window.google?.maps) {
      return;
    }

    setRouteLoading(true);

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: startLocation,
        destination: targetLocation,
        travelMode: window.google.maps.TravelMode.WALKING
      },
      (result, status) => {
        setRouteLoading(false);

        if (status !== window.google.maps.DirectionsStatus.OK || !result?.routes?.length) {
          setRouteError('No walking route found. The pledge will use straight-line distance unless you move the pin.');
          return;
        }

        const routeFeet = result.routes[0].legs.reduce((sum, leg) => {
          return sum + (leg.distance?.value || 0) * 3.28084;
        }, 0);

        setDirections(result);
        setRouteDistanceFeet(Math.max(1, Math.round(routeFeet)));
      }
    );
  }, [startLocation, targetLocation]);

  useEffect(() => {
    if (!Number.isFinite(distanceMiles) || distanceMiles <= 0) {
      return;
    }

    if (timingMode === 'pace') {
      const pace = Number(paceMinutesPerMile);

      if (Number.isFinite(pace) && pace > 0) {
        setTimerMinutes(formatSliderInput(distanceMiles * pace, TIMER_MINUTES_MIN, TIMER_MINUTES_MAX));
      }

      return;
    }

    const minutes = Number(timerMinutes);

    if (Number.isFinite(minutes) && minutes > 0) {
      setPaceMinutesPerMile(formatSliderInput(minutes / distanceMiles, PACE_MINUTES_MIN, PACE_MINUTES_MAX));
    }
  }, [distanceMiles, timingMode, paceMinutesPerMile, timerMinutes]);

  const handlePaceChange = (event) => {
    const nextPace = event.target.value;
    setTimingMode('pace');
    setPaceMinutesPerMile(nextPace);

    const pace = Number(nextPace);
    if (Number.isFinite(distanceMiles) && distanceMiles > 0 && Number.isFinite(pace) && pace > 0) {
      setTimerMinutes(formatSliderInput(distanceMiles * pace, TIMER_MINUTES_MIN, TIMER_MINUTES_MAX));
    }
  };

  const handleTimerChange = (event) => {
    const nextTimer = event.target.value;
    setTimingMode('timer');
    setTimerMinutes(nextTimer);

    const minutes = Number(nextTimer);
    if (Number.isFinite(distanceMiles) && distanceMiles > 0 && Number.isFinite(minutes) && minutes > 0) {
      setPaceMinutesPerMile(formatSliderInput(minutes / distanceMiles, PACE_MINUTES_MIN, PACE_MINUTES_MAX));
    }
  };

  const handleRefreshLocation = async () => {
    setError('');
    setLoadingLocation(true);

    try {
      const location = await getFreshLocation();
      setStartLocation(location);
      setTargetLocation(null);
      setDirections(null);
      setRouteDistanceFeet(null);
      setMapConfirmed(false);
    } catch (locationError) {
      console.error('Error refreshing location:', locationError);
      setError('Unable to get your current location. Enable location services and try again.');
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleUseMapCenterAsStart = () => {
    if (!localTestHost) {
      return;
    }

    setStartLocation(mapCenter);
    setTargetLocation(null);
    setDirections(null);
    setRouteDistanceFeet(null);
    setMapConfirmed(false);
    setError('');
  };

  const handleMapClick = (event) => {
    if (!event.latLng) {
      return;
    }

    setTargetLocation({
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    });
  };

  const handleConfirmMap = () => {
    setMapConfirmed(true);
    setShowMap(false);
  };

  const handleGo = async (event) => {
    event.preventDefault();
    setError('');

    if (!startLocation || !targetLocation) {
      setError('Set your current location and destination pin first');
      return;
    }

    if (!Number.isFinite(Number(timerMinutes)) || Number(timerMinutes) <= 0) {
      setError('Set a valid time limit');
      return;
    }

    setSaving(true);

    try {
      const freshStartLocation = localTestHost
        ? startLocation
        : await getFreshLocation();

      await axios.post(
        `${getApiUrl()}/run-pledges`,
        {
          startLocation: freshStartLocation,
          targetLocation,
          routeDistanceFeet,
          routeTravelMode: routeDistanceFeet ? 'walking' : 'straight_line',
          paceMinutesPerMile: Number(paceMinutesPerMile),
          timerMinutes: Number(timerMinutes),
          amount: Number(amount)
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      navigate('/dashboard');
    } catch (submitError) {
      console.error('Error creating run pledge:', submitError);
      setError(submitError.response?.data?.message || 'Failed to create run pledge');
      setSaving(false);
    }
  };

  if (!canUseRunPledge) {
    return (
      <Page>
        <Shell>
          <Header>
            <HeaderText>
              <BrandHeaderRow>
                <BrandLogo />
                <HeaderActions>
                  <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                    <BackIcon aria-hidden="true" />
                  </HeaderIconButton>
                </HeaderActions>
              </BrandHeaderRow>
              <Title>Run pledge.</Title>
              <Subtitle>This shortcut is only available to Personal App admins.</Subtitle>
            </HeaderText>
          </Header>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell $wide>
        <RunHeader>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Run pledge.</Title>
            <Subtitle>Drop a destination pin, set your pace, then go.</Subtitle>
          </HeaderText>
        </RunHeader>

        <form onSubmit={handleGo}>
          <Stats>
            <Stat
              as="button"
              type="button"
              $interactive
              onClick={() => setShowMap(true)}
              aria-label="Open destination map"
            >
              <StatLabel>Distance</StatLabel>
              <StatValue>{Number.isFinite(distanceFeet) ? formatMiles(distanceFeet) : 'Set...'}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Due time</StatLabel>
              <StatValue>{formatDueTime(dueAt)}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Radius</StatLabel>
              <StatValue>50 ft</StatValue>
            </Stat>
          </Stats>

          {showMap && googleMapsApiKey ? (
            <MapPanel>
              <MapControls>
                <MapSetButton type="button" onClick={handleConfirmMap}>
                  Set
                </MapSetButton>
                <IconButton
                  type="button"
                  onClick={handleRefreshLocation}
                  disabled={loadingLocation}
                  aria-label="Refresh location"
                  title="Refresh location"
                >
                  {loadingLocation ? '...' : '↻'}
                </IconButton>
                {localTestHost && (
                  <MapCenterButton type="button" $secondary onClick={handleUseMapCenterAsStart}>
                    Use map center
                  </MapCenterButton>
                )}
              </MapControls>
              <MapContainer>
                <LoadScript googleMapsApiKey={googleMapsApiKey} version="quarterly">
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={15}
                    onClick={handleMapClick}
                  >
                    {startLocation && (
                      <Marker
                        position={startLocation}
                        label="You"
                      />
                    )}
                    {targetLocation && (
                      <Marker
                        position={targetLocation}
                        draggable
                        label="Go"
                        onDragEnd={(event) => setTargetLocation({
                          lat: event.latLng.lat(),
                          lng: event.latLng.lng()
                        })}
                      />
                    )}
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          suppressMarkers: true,
                          polylineOptions: {
                            strokeColor: '#fbbf24',
                            strokeOpacity: 0.95,
                            strokeWeight: 5
                          }
                        }}
                      />
                    )}
                  </GoogleMap>
                </LoadScript>
              </MapContainer>
            </MapPanel>
          ) : showMap ? (
            <Message $error>Google Maps API key is not configured.</Message>
          ) : null}

          {showPledgeControls && (
            <FormGrid>
              <SliderField>
                <SliderHeader>
                  <span>Pace, minutes per mile</span>
                  <SliderValue>{formatSliderValue(paceMinutesPerMile)}</SliderValue>
                </SliderHeader>
                <RangeInput
                  type="range"
                  min={PACE_MINUTES_MIN}
                  max={PACE_MINUTES_MAX}
                  step="0.1"
                  value={paceMinutesPerMile}
                  onChange={handlePaceChange}
                  onInput={handlePaceChange}
                  aria-label="Pace, minutes per mile"
                  required
                />
              </SliderField>
              <SliderField>
                <SliderHeader>
                  <span>Time limit, minutes</span>
                  <SliderValue>{formatSliderValue(timerMinutes)}</SliderValue>
                </SliderHeader>
                <RangeInput
                  type="range"
                  min={TIMER_MINUTES_MIN}
                  max={TIMER_MINUTES_MAX}
                  step="0.1"
                  value={timerMinutes || TIMER_MINUTES_MIN}
                  onChange={handleTimerChange}
                  onInput={handleTimerChange}
                  aria-label="Time limit, minutes"
                  required
                />
              </SliderField>
              <SliderField>
                <SliderHeader>
                  <span>Cost if failed</span>
                  <SliderValue>{formatSliderValue(amount, { prefix: '$' })}</SliderValue>
                </SliderHeader>
                <RangeInput
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  onInput={(event) => setAmount(event.target.value)}
                  aria-label="Cost if failed"
                  required
                />
              </SliderField>
            </FormGrid>
          )}

          {error && <Message $error>{error}</Message>}
          {routeError && <Message $error>{routeError}</Message>}

          {showPledgeControls && (
            <PrimaryAction>
              <StartButton type="submit" disabled={saving || routeLoading || !targetLocation || !startLocation || !hasDistance}>
                {saving ? 'Creating...' : routeLoading ? 'Routing...' : 'Start Run Pledge'}
              </StartButton>
            </PrimaryAction>
          )}
        </form>
      </Shell>
    </Page>
  );
};

export default RunPledge;
